import React from 'react';
import { Typography } from '@mui/material';
import WidgetWrapper from './WidgetWrapper';

// Helper function to format numbers with thousand separators
// Default format: Turkish style (dot as thousands, comma as decimal, 2 decimals)
const formatNumber = (value, format) => {
  if (typeof value !== 'number') return value;

  // Use provided format or default Turkish format
  const { decimals = 2, decimalSeparator = ',', thousandsSeparator = '.' } = format || {};

  // Round to specified decimals
  const rounded = Number(value.toFixed(decimals));

  // Split into integer and decimal parts
  const [intPart, decPart] = rounded.toFixed(decimals).split('.');

  // Add thousands separator to integer part
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);

  // Combine with decimal part using specified separator
  return decPart ? `${formattedInt}${decimalSeparator}${decPart}` : formattedInt;
};

const SimpleTextLabel = ({
  text = '',
  textAlign = 'left',
  tagValue = null,
  value = null, // Support 'value' prop (from tagBinding.property)
  parentText = null, // Parent widget text (for ALARM widgets)
  unit = '', // Unit suffix (e.g., "°C", "kW", "mg/L")
  valueMap = null, // Value mapping: { "0": "OFF", "1": "ON" } - maps raw values to display text
  numberFormat = null, // Number format object {decimals, decimalSeparator, thousandsSeparator}
  tagId = null, // Tag ID for context menu
  // Status label support (originalOptions format)
  isStatusLabel = false,
  originalOptions = null,
  // Status label support (colorMap format - alternative)
  colorMap = null,
  textColorMap = null,
  valueTextMap = null,
  // Style properties
  backgroundColor,
  foreColor,
  borderWidth,
  borderColor,
  borderRadius,
  fontSize,
  fontWeight,
  fontFamily,
  opacity,
  ...props
}) => {
  const justifyContent = textAlign === 'center' ? 'center' : textAlign === 'right' ? 'flex-end' : 'flex-start';

  // Helper to unwrap tag value objects
  const unwrap = (v) => {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      if (v.value !== undefined) return v.value;
      if (v.raw !== undefined) return v.raw;
    }
    return v;
  };

  // Get the actual tag value (unwrap objects, fallback to text if it looks like a tag value)
  const rawActual = value !== null && value !== undefined ? value
    : tagValue !== null && tagValue !== undefined ? tagValue
    : null;
  const actualValue = unwrap(rawActual);


  // Context menu handler - dispatches global event
  const handleContextMenu = (event) => {
    if (tagId) {
      event.preventDefault();
      event.stopPropagation();

      // Dispatch global event to open Tag Info Modal
      const displayName = (text && text !== 'Label') ? text : `Tag ${tagId}`;
      const tagInfoData = {
        tagId: tagId,
        tagName: displayName,
        tagName2: '',
        currentValue: actualValue,
        unit: unit || ''
      };
      window.dispatchEvent(new CustomEvent('openTagInfo', { detail: tagInfoData }));
    }
  };

  // 🏷️ STATUS LABEL: Use dynamic text from originalOptions or colorMap
  let displayText;
  let statusLabelBgColor = null;
  let statusLabelFgColor = null;

  // 🗺️ VALUE MAP: Check valueMap first (highest priority for text mapping)
  // Use actualValue if available, otherwise try text (bindings may put value into text prop)
  const valueForMap = actualValue !== null && actualValue !== undefined ? actualValue : text;
  if (valueMap && valueForMap !== null && valueForMap !== undefined) {
    const valueKey = String(valueForMap).trim();
    // Try exact match first
    if (valueMap[valueKey] !== undefined) {
      displayText = valueMap[valueKey];
    } else {
      // Try parsing as number and matching
      const numVal = parseFloat(valueKey);
      if (!isNaN(numVal)) {
        const numericKey = String(Math.round(numVal));
        if (valueMap[numericKey] !== undefined) {
          displayText = valueMap[numericKey];
        }
      }
    }
    // Apply colorMap alongside valueMap (renk + text mapping birlikte)
    if (colorMap) {
      const ck = String(valueForMap).trim();
      statusLabelBgColor = colorMap[ck] || null;
      if (!statusLabelBgColor) {
        const nv = parseFloat(ck);
        if (!isNaN(nv)) statusLabelBgColor = colorMap[String(Math.round(nv))] || null;
      }
      if (textColorMap) {
        statusLabelFgColor = textColorMap[ck] || null;
        if (!statusLabelFgColor) {
          const nv = parseFloat(ck);
          if (!isNaN(nv)) statusLabelFgColor = textColorMap[String(Math.round(nv))] || null;
        }
      }
    }
  }
  // Check colorMap format (newer format from converter)
  else if (colorMap && actualValue !== null && actualValue !== undefined) {
    const valueStr = String(actualValue);

    // Get colors from colorMap and textColorMap
    statusLabelBgColor = colorMap[valueStr] || null;
    statusLabelFgColor = textColorMap ? textColorMap[valueStr] : null;

    // Get display text from valueTextMap or format the value
    if (valueTextMap) {
      // Use mapped text if available
      displayText = valueTextMap[valueStr];
    } else {
      // Format the number if value is numeric (always apply default Turkish format)
      const numValue = Number(actualValue);
      const formattedValue = !isNaN(numValue)
        ? formatNumber(numValue, numberFormat)
        : valueStr;

      // Add unit if defined
      displayText = (unit && unit !== '' && unit !== 'None')
        ? `${formattedValue} ${unit}`
        : formattedValue;
    }
  }
  // Fallback to originalOptions format (older format)
  else if (isStatusLabel && originalOptions && Array.isArray(originalOptions) && actualValue !== null && actualValue !== undefined) {
    const valueStr = String(actualValue);
    const matchingOption = originalOptions.find(opt => String(opt.value) === valueStr);

    if (matchingOption) {
      displayText = matchingOption.label;
      statusLabelBgColor = matchingOption.color;
      statusLabelFgColor = matchingOption.textColor;
    } else {
      // Default to first option
      displayText = originalOptions[0].label;
      statusLabelBgColor = originalOptions[0].color;
      statusLabelFgColor = originalOptions[0].textColor;
    }
  } else {
    // Prioritize: value > tagValue > text
    const rawValue = value !== null && value !== undefined
      ? value
      : tagValue !== null && tagValue !== undefined
        ? tagValue
        : text;

    // Format number and add unit if we have a numeric tag value
    if ((value !== null && value !== undefined) || (tagValue !== null && tagValue !== undefined)) {
      // Format the number if value is numeric (always apply default Turkish format)
      const numValue = Number(rawValue);
      const formattedValue = !isNaN(numValue)
        ? formatNumber(numValue, numberFormat)
        : String(rawValue);

      // Add unit if defined
      displayText = (unit && unit !== '' && unit !== 'None')
        ? `${formattedValue} ${unit}`
        : formattedValue;
    } else {
      displayText = String(rawValue);
    }

    // For ALARM widgets, map numeric values to text
    const widgetTypeText = (parentText || text || '').toString().toUpperCase();
    if ((widgetTypeText === 'ALARM' || widgetTypeText === 'ARIZA') && actualValue !== null && actualValue !== undefined) {
      const numericValue = parseFloat(actualValue);
      if (numericValue === 0 || String(actualValue).toUpperCase().trim() === '0') {
        displayText = 'NORMAL';
      } else if (numericValue === 1 || String(actualValue).toUpperCase().trim() === '1') {
        displayText = 'ALARM';
      }
    }
  }

  // Dynamic backgroundColor for ALARM/NORMAL widgets
  const getDynamicBackgroundColor = () => {
    // 🏷️ STATUS LABEL: Highest priority (both colorMap and originalOptions formats)
    if (statusLabelBgColor) {
      return statusLabelBgColor;
    }

    // Check if this is an ALARM widget by:
    // 1. Static backgroundColor is red (indicates ALARM widget type)
    // 2. OR text matches ALARM/ARIZA/NORMAL (indicating it's part of an ALARM status system)
    const isRedBackground = backgroundColor && (
      backgroundColor.toLowerCase() === '#ff2400' ||
      backgroundColor.toLowerCase().includes('rgb(255') ||
      backgroundColor.toLowerCase() === 'red'
    );

    const widgetTypeText = (parentText || text || displayText || '').toString().toUpperCase();
    const isAlarmWidget =
      isRedBackground ||
      widgetTypeText === 'ALARM' ||
      widgetTypeText === 'ARIZA' ||
      widgetTypeText === 'NORMAL';

    // If this is an ALARM-type widget and has a tag value, determine color based on value
    if (isAlarmWidget && actualValue !== null && actualValue !== undefined) {
      const numericValue = parseFloat(actualValue);
      const stringValue = String(actualValue).toUpperCase().trim();

      // 0 = NORMAL = white, 1 = ALARM = red
      if (numericValue === 0 || stringValue === 'NORMAL' || stringValue === '0') {
        return '#ffffff';
      } else if (numericValue === 1 || stringValue === 'ALARM' || stringValue === 'ARIZA' || stringValue === '1') {
        return '#ff2400';
      }
    }

    // Default: use the static backgroundColor
    return backgroundColor;
  };

  const finalBackgroundColor = getDynamicBackgroundColor();
  // Use statusLabelFgColor if set (from either colorMap or originalOptions)
  const finalForeColor = statusLabelFgColor ? statusLabelFgColor : foreColor;
  // Apply border styling if using colorMap or isStatusLabel
  const hasStatusLabel = (colorMap || isStatusLabel);
  const finalBorderColor = hasStatusLabel ? (borderColor || '#000000') : borderColor;
  const finalBorderRadius = hasStatusLabel ? (borderRadius || 4) : borderRadius;

  return (
    <>
      <WidgetWrapper
        backgroundColor={finalBackgroundColor}
        foreColor={finalForeColor}
        borderWidth={borderWidth}
        borderColor={finalBorderColor}
        borderRadius={finalBorderRadius}
        fontSize={fontSize}
        fontWeight={fontWeight}
        fontFamily={fontFamily}
        opacity={opacity}
        justifyContent={justifyContent}
        padding="4px"
        onContextMenu={handleContextMenu}
        {...props}
      >
      <Typography
        style={{
          fontSize: 'inherit',
          fontWeight: 'inherit',
          color: 'inherit',
          fontFamily: 'inherit',
          textAlign,
          wordBreak: 'break-word',
          width: '100%',
          height: 'calc(100% - 5px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent  // Add justifyContent for flex alignment
        }}
      >
        {displayText}
      </Typography>
    </WidgetWrapper>
  </>
  );
};

export default SimpleTextLabel;